import { ProductReportCtaBanner } from "@/components/site/ProductReportCtaBanner";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { stateKeywordsMeta } from "@/lib/page-keywords";
import { getProduct } from "@/data/products";
import { getProductHero } from "@/data/productHeroes";
import { getStateHero } from "@/data/state-heroes";
import { getState } from "@/data/states";
import { getMarketStats } from "@/lib/market-stats.functions";
import { getHousingMomentum } from "@/lib/housing-momentum.functions";
import { getCountyInsights } from "@/lib/county-insights.functions";
import { BridgeStatePage } from "@/components/site/BridgeStatePage";
import { bridgeStateFaqJsonLd, bridgeStateMetaDescription, stateFaq } from "@/components/site/BridgeStatePage.seo";
import { productHowToJsonLd, productArticleJsonLd } from "@/components/site/ProductPage.seo";
import { buildHead } from "@/lib/seo-head";

const SLUG = "bridge-loans";
const PRODUCT = getProduct(SLUG)!;
const HERO = getProductHero(SLUG)!;

export const Route = createFileRoute("/bridge-loans/$state/")({
  loader: async ({ params }) => {
    const state = getState(params.state);
    if (!state) throw notFound();
    const [liveStats, housingMomentum, countyInsights] = await Promise.all([
      getMarketStats({ data: { stateSlug: params.state } }).catch(() => null),
      getHousingMomentum({ data: { stateAbbrev: state.abbrev, stateName: state.name } }).catch(() => null),
      getCountyInsights({ data: { stateAbbrev: state.abbrev, placeLabel: state.name } }).catch(() => null),
    ]);
    return { state, liveStats, housingMomentum, countyInsights };
  },
  head: ({ params, loaderData }) => {
    const state = loaderData?.state;
    if (!state) return { meta: [{ title: `Bridge loans | Simply Approved` }, { name: "robots", content: "noindex" }] };
    const url = `/${SLUG}/${params.state}`;
    const stateHero = getStateHero(params.state) ?? HERO;
    const title = `${state.name} Bridge Loans | 80% LTV · 10-Day Close · DSCR Takeout | Simply Approved`;
    const description = bridgeStateMetaDescription(state);
    return buildHead({
      title,
      description,
      url,
      keywords: stateKeywordsMeta(PRODUCT.slug, state.name),
      image: stateHero.src,
      imageAlt: stateHero.alt,
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: PRODUCT.shortName, url: `/${SLUG}` },
        { name: state.name, url },
      ],
      faqs: stateFaq(state),
      service: { name: `Bridge Loans in ${state.name}`, state: state.name },
      extraJsonLd: [
        productHowToJsonLd(PRODUCT, url),
        productArticleJsonLd(PRODUCT, url),
      ],
    });
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { state, liveStats, housingMomentum, countyInsights } = Route.useLoaderData();
  return (
    <BridgeStatePage
      product={PRODUCT}
      state={state}
      liveStats={liveStats}
      housingMomentum={housingMomentum}
      countyInsights={countyInsights}
      midSlot={<ProductReportCtaBanner productKey="bridge" variant="full" locationLabel={state.name} />}
    />
  );
}
